"use client";

import { type ComponentProps, forwardRef, useId } from "react";
import { type FieldValues, type RegisterOptions, useFormContext } from "react-hook-form";
import { cn } from "../../../..";

interface RadioGroupItemProps extends ComponentProps<"div"> {
	name: string;
	value: string;
	registerOptions?: RegisterOptions<FieldValues>;
	bulletPosition?: "top" | "middle";
	showIndicator?: boolean;
}

export const NativeRadioGroupItem = forwardRef<HTMLInputElement, RadioGroupItemProps>(
	function ItemRef(props, ref) {
		const {
			name,
			value,
			registerOptions,
			bulletPosition = "middle",
			showIndicator = false,
			className,
			children,
			...restProps
		} = props;

		const id = useId();
		const methods = useFormContext();
		const registeredProps = methods
			? methods.register(name, { shouldUnregister: true, ...registerOptions })
			: { ref };
		const methodValue: unknown = methods?.getValues(name);

		return (
			<label
				htmlFor={id}
				className={cn(
					"relative flex cursor-pointer appearance-none items-center rounded border-2 border-shades bg-white p-4 focus:outline-none focus-visible:border-primary-medium motion-safe:transition",
					{ "border-primary-medium bg-shades-light": methodValue === value },
				)}
			>
				{showIndicator && (
					<div
						className={cn(
							"absolute left-3 h-4 w-4 transform rounded-full bg-white ring-2 ring-primary-medium ring-offset-2 motion-safe:transition",
							{
								"bg-primary-medium": methodValue === value,
								"top-5": bulletPosition === "top",
								"-translate-y-1/2 top-1/2": bulletPosition === "middle",
							},
						)}
					/>
				)}

				<input type="radio" id={id} value={value} className="hidden" {...registeredProps} />

				<div className={cn("h-full w-full", { "ml-6": showIndicator }, className)} {...restProps}>
					{children}
				</div>
			</label>
		);
	},
);
